<template>
  <v-form
    lazy-validation
    v-model="valid"
    @submit.prevent="submit"
    ref="form"
  >
      <v-container class="text-center">
        <h2>Dried Abalone Cooking Sheet</h2>
        <v-row>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneCookingSheet-farm"
              :label="$$t('farm')"
              v-model="localDriedAbaloneCookingSheet.farm"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneCookingSheet-deliveryDate"
              :label="$$t('deliveryDate')"
              v-model="localDriedAbaloneCookingSheet.deliveryDate"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneCookingSheet-drySizeRange"
              :label="$$t('drySizeRange')"
              v-model="localDriedAbaloneCookingSheet.drySizeRange"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneCookingSheet-cookingDate"
              :label="$$t('cookingDate')"
              v-model="localDriedAbaloneCookingSheet.cookingDate"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneCookingSheet-liveWeight"
              :label="$$t('liveWeight')"
              v-model="localDriedAbaloneCookingSheet.liveWeight"
              type="number"
            />
          </v-col>
        </v-row>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th></th>
                <th class="text-left"
                  v-for="(heading, index) in headings"
                  :key="`heading-${index}`">
                  {{ heading.text }}
                </th>
              </tr>
              <tr>
                <th></th>
                <th class="text-left" :style="`background-color: ${size.color};`"
                  v-for="(size, index) in sizes1"
                  :key="`heading-${index}`">
                  {{ size.text }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, lineIndex) in items1"
                :key="item.id"
              >
                <td></td>
                <td
                  v-for="(heading, index) in headings"
                  :key="`item-${lineIndex}-${index}`">
                  <v-text-field
                    @change="updateTotals()"
                    v-model="item[`${heading.value}_${sizes1[index].value}`]"
                  />
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th>
                  Totals
                </th>
                <th class="text-left"
                  v-for="(heading, index) in headings"
                  :key="`heading-${index}`">
                  <span>
                    {{ getTotal(`${heading.value}_${sizes1[index].value}`)}}
                  </span>
                </th>
              </tr>
              <tr>
                <td colspan="6">
                  <v-btn color="success" @click="items1.push({})">New Line</v-btn>
                </td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th></th>
                <th class="text-left"
                  v-for="(heading, index) in headings"
                  :key="`heading-${index}`">
                  {{ heading.text }}
                </th>
              </tr>
              <tr>
                <th></th>
                <th class="text-left" :style="`background-color: ${size.color};`"
                  v-for="(size, index) in sizes2"
                  :key="`heading-${index}`">
                  {{ size.text }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, lineIndex) in items2"
                :key="item.id"
              >
                <td></td>
                <td
                  v-for="(heading, index) in headings"
                  :key="`item-${lineIndex}-${index}`"
                  >
                  <v-text-field
                    @change="updateTotals()"
                    v-model="item[`${heading.value}_${item.text}`]"
                  />
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th>
                  Totals
                </th>
                <th class="text-left"
                  v-for="(heading, index) in headings"
                  :key="`heading-${index}`">
                  <span>
                    {{ getTotal(`${heading.value}_${sizes2[index].value}`)}}
                  </span>
                </th>
              </tr>
              <tr>
                <td colspan="6">
                  <v-btn color="success" @click="items2.push({})">New Line</v-btn>
                </td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
        <v-text-field
          filled
          data-cy="FormDriedAbaloneCookingSheet-underSizedWeight"
          :label="$$t('underSizedWeight')"
          v-model="localDriedAbaloneCookingSheet.underSizedWeight"
          @change="updateTotals()"
          type="number"
        />
        <v-text-field
          filled
          data-cy="FormDriedAbaloneCookingSheet-totalStringedWeight"
          :label="$$t('totalStringedWeight')"
          v-model="localDriedAbaloneCookingSheet.totalStringedWeight"
          type="number"
        />
        <div class="d-flex justify-end mb-6">
          <v-btn color="default" outlined class="ml-4 btn-cancel" @click="cancel"
            data-cy="FormDriedAbaloneCookingSheet-button-cancel">
            {{ $t('common.buttons.cancel') }}
          </v-btn>
          <v-btn color="primary" type="submit" class="ml-4" :loading="isSaving"
            data-cy="FormDriedAbaloneCookingSheet-button-save">
              {{ $t('common.buttons.save') }}
            </v-btn>
        </div>
      </v-container>
  </v-form>
</template>

<script>
import loading from '@codehq/aurora-app-core/src/mixins/loading';

export default {
  name: 'FormDriedAbaloneCookingSheet',
  module: 'DriedAbaloneCookingSheet',
  mixins: [loading],
  props: {
    /**
    * Set the text box value. Default: undefined,
    */
    DriedAbaloneCookingSheet: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      /**
      * Hold form validation state
      */
      valid: false,
      /**
      * Local value used for property localDriedAbaloneCookingSheet
      */
      localDriedAbaloneCookingSheet: {},
      items1: [{}],
      items2: [{}],
      newItem: {},
    };
  },
  computed: {
    /**
     * Returns the headings property
     */
    headings() {
      return [{
        text: 'Out of Salt',
        value: 'outOfSalt',
      }, {
        text: 'After Soaking',
        value: 'afterSoaking',
      }, {
        text: 'Out of Brine',
        value: 'outOfBrine',
      }, {
        text: 'After Scrubbing',
        value: 'afterScrubbing',
      }, {
        text: 'After Cooking',
        value: 'afterCooking',
      }];
    },
    sizes1() {
      return [{
        text: '26-35',
        color: '#ff99cc',
        value: '26_35',
      }, {
        text: '36-43',
        color: '#ff9900',
        value: '36_43',
      }, {
        text: '44-51',
        color: '#ffff5d',
        value: '44_51',
      }, {
        text: '52-64',
        color: '#ff6161',
        value: '52_64',
      }, {
        text: '65-77',
        color: '#69b4ff',
        value: '65_77',
      }];
    },
    sizes2() {
      return [{
        text: '78-90',
        color: '#65d965',
      }, {
        text: '91-101',
        color: '#ffffff',
      }, {
        text: '102-114',
        color: '#dc97ff',
      }, {
        text: '115-127',
        color: '#f6b600',
      }, {
        text: '128+',
        color: '#d9d9d9',
      }];
    },
  },
  watch: {
    DriedAbaloneCookingSheet() {
      this.localDriedAbaloneCookingSheet = this.DriedAbaloneCookingSheet;
    },
  },
  created() {
    this.localDriedAbaloneCookingSheet = this.DriedAbaloneCookingSheet;
  },
  methods: {
    /** Submit Form data trigger
     * @event save
     * @type {object}
     */
    submit() {
      // validate
      if (this.$refs.form.validate()) {
        this.$emit('save', this.localDriedAbaloneCookingSheet);
        this.isSaving = true;
      }
    },
    /** Cancel form trigger
     * @event cancel
     */
    cancel() {
      this.$emit('cancel');
    },
    getTotal(prop) {
      let total = 0;
      this.items1.forEach((item) => {
        total += parseFloat(item[prop] ?? 0);
      });
      this.items2.forEach((item) => {
        total += parseFloat(item[prop] ?? 0);
      });
      return total;
    },
    updateTotals() {
      console.debug('updateTotals');
      let total = 0;
      total += parseFloat(this.localDriedAbaloneCookingSheet.underSizedWeight ?? 0);
      this.items1.forEach((item) => {
        // loop through each property of item and parse it to float and add it to total
        Object.keys(item).forEach((key) => {
          console.debug('total', total);
          total += parseFloat(item[key] ?? 0);
        });
        console.debug('total', total);
      });
      this.items2.forEach((item) => {
        Object.keys(item).forEach((key) => {
          console.debug('total', total);
          total += parseFloat(item[key] ?? 0);
        });
        console.debug('total', total);
      });
      console.debug('total', total);
      this.localDriedAbaloneCookingSheet.totalStringedWeight = total.toFixed(3);
      this.$forceUpdate();
    },
  },
};
</script>

<docs>
# FormDriedAbaloneCookingSheet example

'''js
<FormDriedAbaloneCookingSheet :DriedAbaloneCookingSheet="DriedAbaloneCookingSheet" />
'''
</docs>
